import { Box, Button, TextField } from '@mui/material'
import { PaidRounded, PrintRounded } from '@mui/icons-material'
import { DesktopDatePicker } from '@mui/x-date-pickers-pro'
import { BoxFC, BoxFR } from 'components/BoxCustom'
import DataGridCellExpand from 'components/DataGridCellExpand/DataGridCellExpand'
import ComboBox from 'components/ComboBox'
import SearchButton from 'components/buttons/SearchButton'
import ClearButton from 'components/buttons/ClearButton'
import { GlobalStateContext } from 'contexts/GlobalStateContext'
import { alertConfirm, alertError, alertSuccess } from 'components/Alert'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { red } from '@mui/material/colors'
import { addIdForDataGrid } from 'utils'
import { addSumRow, getSumColorClassName, sxSumColorClass } from 'utils/highlightSumColor'
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid'
import { initFilterData, clearFilterData } from './initData'
import { driverSalaryColumns } from './columns'
import PayrollDetailDialog from './PayrollDetailDialog'
import AddPayrollDialog from './AddPayrollDialog'
import { printDriverSalaryV3Summary } from './printDriverSalaryV3Summary'
import { printDriverSalaryV3Detail } from './printDriverSalaryV3Detail'

let selectedRow = {}
const SS_PRCT_KEY = "driverPayrollV3SSPrct"

const DriverSalaryV3 = () => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [expColumns, setExpColumns] = useState([])
  const [filterData, setFilterData] = useState({ ...initFilterData })
  const [selectionModel, setSelectionModel] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [payRef, setPayRef] = useState("")
  // ปกส(%) persisted to localStorage; loaded on first render
  const [ssPrct, setSsPrct] = useState(() => localStorage.getItem(SS_PRCT_KEY) ?? "5")

  const handleChangeSsPrct = useCallback((e) => {
    setSsPrct(e.target.value)
    localStorage.setItem(SS_PRCT_KEY, e.target.value)
  }, [])

  // getPayrolls returns { rows, expColumns } — the column set comes from the expense master
  // so every type-20/21 expense shows (even empty) and can be filled in inline.
  const applyData = useCallback((data) => {
    setDataTable(addIdForDataGrid(data.rows || [], 'DrvId'))
    setExpColumns(data.expColumns || [])
  }, [])

  const getData = useCallback((filter) => {
    ax.post("/branch/driver-payroll-v3/getPayrolls", {
      SalDte: dayjs(filter.SalDte).format("YYYY-MM-DD"),
      DrvId: filter.DrvId,
    }).then(value => {
      if (value.data) { applyData(value.data); setSelectionModel([]) }
    })
  }, [ax, applyData])

  // build actions run on the selected drivers (buttons disabled when none selected)
  const handleBulkBuild = useCallback((name, okMsg, extra = {}) => {
    const drvIds = selectionModel.map(Number)
    if (drvIds.length === 0) return
    ax.post(`/branch/driver-payroll-v3/${name}`, {
      SalDte: dayjs(filterData.SalDte).format("YYYY-MM-DD"), DrvIds: drvIds, ...extra,
    }).then(value => { if (value.data) { alertSuccess(okMsg); getData(filterData) } })
  }, [ax, selectionModel, filterData, getData])

  const handleBulkConfirm = useCallback(() => {
    const drvIds = selectionModel.map(Number)
    const payrollIds = dataTable.filter(r => drvIds.includes(r.DrvId) && r.PayrollSts === 1).map(r => r.PayrollId)
    if (payrollIds.length === 0) { alertError("เลือกเฉพาะงวดร่าง (ยังไม่ยืนยัน)"); return }
    alertConfirm(`ยืนยันเงินเดือน ${payrollIds.length} รายการ? เมื่อยืนยันแล้วจะแก้ไขไม่ได้`, () => {
      ax.post("/branch/driver-payroll-v3/confirmPayroll", { PayrollIds: payrollIds })
        .then(value => { if (value.data) { alertSuccess("ยืนยันเงินเดือนแล้ว"); getData(filterData) } })
    })
  }, [ax, selectionModel, dataTable, filterData, getData])

  const handlePaySelected = useCallback(() => {
    const payrollIds = dataTable.filter(r => selectionModel.includes(r.id) && r.PayrollSts === 2).map(r => r.PayrollId)
    if (payrollIds.length === 0) { alertError("เลือกเฉพาะงวดที่ยืนยันแล้ว (ยังไม่จ่าย)"); return }
    alertConfirm(`จ่ายเงินเดือน ${payrollIds.length} รายการ?`, () => {
      ax.post("/branch/driver-payroll-v3/payPayroll", {
        PayrollIds: payrollIds, PayRef: payRef || null, PayDte: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      }).then(value => { if (value.data) { alertSuccess("จ่ายเงินเดือนแล้ว"); getData(filterData) } })
    })
  }, [ax, dataTable, selectionModel, payRef, filterData, getData])

  // pull each selected driver's payroll via getPayrollDetail — both prints are fed from the
  // same payload; they differ only in what they draw (3 slips a page vs 1 driver a page + detail)
  const loadPrintData = useCallback(async () => {
    const rows = dataTable.filter(r => r.id !== 0 && selectionModel.includes(r.id))
    if (rows.length === 0) { alertError("กรุณาเลือกพนักงานขับรถ"); return [] }
    const salDte = dayjs(filterData.SalDte).format("YYYY-MM-DD")
    const payrolls = []
    for (const gridRow of rows) {
      const res = await ax.post("/branch/driver-payroll-v3/getPayrollDetail",
        { PayrollId: gridRow.PayrollId, DrvId: gridRow.DrvId, SalDte: salDte }, false)
      if (res.data) payrolls.push({ gridRow, payroll: res.data.payroll, items: res.data.items || [] })
    }
    return payrolls
  }, [ax, dataTable, selectionModel, filterData])

  // payslips only — 3 per A4 page
  const handlePrint = useCallback(async () => {
    const payrolls = await loadPrintData()
    if (payrolls.length) printDriverSalaryV3Summary({ msData, payrolls, expColumns })
  }, [loadPrintData, msData, expColumns])

  // same payslip header, one driver per page, with the ค่าเที่ยว lines listed underneath
  const handlePrintDetail = useCallback(async () => {
    const payrolls = await loadPrintData()
    if (payrolls.length) printDriverSalaryV3Detail({ msData, payrolls, expColumns })
  }, [loadPrintData, msData, expColumns])

  const handleOpenDetail = useCallback((row) => { selectedRow = row; setDialogOpen(true) }, [])

  const columns = useMemo(() => driverSalaryColumns(handleOpenDetail, expColumns), [handleOpenDetail, expColumns])
  // the freshest grid row for the opened driver — feeds the dialog summary boxes (advance draws,
  // trip counts, fuel) which live on the list row, not on getPayrollDetail
  const detailRow = useMemo(
    () => dataTable.find(r => r.DrvId === selectedRow.DrvId) || selectedRow, [dataTable, dialogOpen]) // eslint-disable-line react-hooks/exhaustive-deps
  const noSelection = selectionModel.length === 0

  // pinned sum row (id 0): total every expense column + the summary totals
  const modDataTable = useMemo(() => addSumRow({
    dataTable,
    sumColNms: [...expColumns.filter(c => c.kind !== "dscp").map(c => c.key),
      "NumSts1", "NumSts3", "NumSts4", "NumSts5",
      "SumLitr", "TotEarnAmnt", "TotDeAmnt", "AdvTripAmnt", "NetAmnt"],
    sumColTotal: "DrvNm",
  }), [dataTable, expColumns])

  const sumRow = useMemo(() => modDataTable.find(r => r.id === 0), [modDataTable])

  // editing a type-20/21 cell upserts that salary line; applies to all selected drivers
  const isCellEditable = useCallback((params) => params.row.id !== 0 && params.row.PayrollSts < 2, [])
  const handleCellEditCommit = useCallback(({ id, field, value }) => {
    // exp_<ExpId> = the amount, dscp_<ExpId> = that line's หมายเหตุ (หักอื่นๆ)
    const isDscp = !!field && field.startsWith("dscp_")
    if (!field || (!isDscp && !field.startsWith("exp_"))) return
    const ExpId = +field.slice(isDscp ? 5 : 4)
    const drvIds = selectionModel.includes(id) ? selectionModel.map(Number) : [Number(id)]
    const salDte = dayjs(filterData.SalDte).format("YYYY-MM-DD")
    ax.post(`/branch/driver-payroll-v3/${isDscp ? "saveSalaryItemDscp" : "saveSalaryItem"}`, {
      DrvIds: drvIds, ExpId, SalDte: salDte,
      ...(isDscp ? { Dscp: value === "" ? null : value } : { Amnt: value === "" ? null : value }),
      getArgs: { SalDte: salDte, DrvId: filterData.DrvId },
    }, false).then(res => { if (res.data) applyData(res.data) })
  }, [ax, selectionModel, filterData, applyData])

  const monthPickerProp = {
    disableMaskedInput: true, openTo: "month", views: ["year", "month"], label: "เดือนเงินเดือน",
    inputFormat: "MMMM YYYY", value: filterData.SalDte,
    onChange: (v) => setFilterData(o => ({ ...o, SalDte: v })),
    renderInput: (params) => <TextField size="small" {...params} sx={{ width: 170 }} />,
  }

  useEffect(() => { getData({ ...initFilterData }) }, [getData])

  return (
    <BoxFC height='100%'>
      <BoxFR>
        <Box flex={1} />
        <TextField size="small" sx={{ width: 130 }} label="อ้างอิงการจ่าย" value={payRef}
          onChange={e => setPayRef(e.target.value)} />
        <Button variant="contained" color="success" startIcon={<PaidRounded />} onClick={handlePaySelected}>
          จ่ายที่เลือก
        </Button>
        <ComboBox sx={{ width: 170 }} options={msData.driverOnlyCombo} label="พนักงานขับรถ"
          selectedId={filterData.DrvId} setSelectedId={(id) => setFilterData(o => ({ ...o, DrvId: id }))} />
        <DesktopDatePicker {...monthPickerProp} />
        <SearchButton onClick={() => getData(filterData)} />
        <ClearButton onClick={() => setFilterData({ ...clearFilterData })} />
      </BoxFR>

      <BoxFR sx={{ flexWrap: "wrap" }}>
        <Button variant="contained" color="secondary"
          onClick={() => setAddOpen(true)}>สร้างงวด</Button>
        <Button variant="contained" color="info" disabled={noSelection}
          onClick={handleBulkConfirm}>ยืนยัน</Button>
        <Button variant="contained" disabled={noSelection}
          onClick={() => handleBulkBuild("pullPrevMonth", "ดึงข้อมูลเดือนก่อนหน้าแล้ว")}>ดึงเดือนก่อนหน้า</Button>
        <Button variant="contained" disabled={noSelection}
          onClick={() => handleBulkBuild("settleAdvances", "หักเงินเบิกล่วงหน้าแล้ว")}>หักเบิกล่วงหน้า</Button>
        <TextField size="small" label="ปกส(%)" sx={{ width: 80 }} type="number"
          value={ssPrct} onChange={handleChangeSsPrct} />
        <Button variant="contained" disabled={noSelection}
          onClick={() => handleBulkBuild("calcSS", "คำนวณประกันสังคมแล้ว", { DeSSPrct: +ssPrct })}>คำนวณ ปกส.</Button>
        <Button variant="contained" color="warning" sx={{display: "none"}} startIcon={<PrintRounded />} disabled={noSelection}
          onClick={handlePrint}>พิมพ์รายงานเงินเดือน</Button>
        <Button variant="contained" color="warning" startIcon={<PrintRounded />} disabled={noSelection}
          onClick={handlePrintDetail}>พิมพ์พร้อมค่าเที่ยว</Button>
      </BoxFR>
      <Box height='100%' sx={{ ...sxSumColorClass, mt: 1, "& .exp-deduct": { color: red[700] } }}>
        <DataGridCellExpand
          experimentalFeatures={{ rowPinning: true }}
          hideFooter
          checkboxSelection
          isRowSelectable={(params) => params.row.id !== 0}
          isCellEditable={isCellEditable}
          selectionModel={selectionModel}
          onSelectionModelChange={(ids) => setSelectionModel(ids)}
          rows={dataTable}
          columns={columns}
          onCellEditCommit={handleCellEditCommit}
          pinnedRows={{ bottom: sumRow ? [sumRow] : [] }}
          getRowClassName={getSumColorClassName}
          initialState={{
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'ShowDetail', 'No', 'DrvCd', 'DrvNm'],
            },
          }}
        />
      </Box>
      <PayrollDetailDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        drvId={selectedRow.DrvId}
        drvNm={selectedRow.DrvNm}
        salDte={filterData.SalDte}
        gridRow={detailRow}
        expColumns={expColumns}
        onFinish={() => getData(filterData)}
      />
      <AddPayrollDialog
        dialogOpen={addOpen}
        setDialogOpen={setAddOpen}
        exDrvIds={dataTable.map(r => r.DrvId)}
        salDte={filterData.SalDte}
        onFinish={() => getData(filterData)}
      />
    </BoxFC>
  )
}

export default DriverSalaryV3
